import './optimizerStyles.css'

import { CloseOutlined } from '@ant-design/icons'
import { useAutoDispatch } from '@api/useAutoDispatch'
import { CreateNewRun } from '@components/OrderDispatch/OptimizerWizard/Create/CreateNewRun'
import { HistoryPanel } from '@components/OrderDispatch/OptimizerWizard/HistoryPanel'
import { ReviewResults } from '@components/OrderDispatch/OptimizerWizard/Results/ReviewResults'
import { useRouteOptimization } from '@hooks/useRouteOptimization'
import { GraviButton, Horizontal, NotificationMessage, Texto, Vertical } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import { Drawer, Spin } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useLockedOrders } from '@hooks/useLockedOrders'
import { useLockedSchedules } from '@hooks/useLockedSchedules'
import { type OptimizerWizardProps, type ReviewResultsProps, ROUTE_OPTIMIZER_MODE } from './types'

export function OptimizerWizard({
  mode,
  setMode,
  depots,
  visible,
  setVisible,
  schedulePayload,
  refetchOrders,
  refetchDriverSchedule,
  ordersFromDispatch,
}: OptimizerWizardProps) {
  const [selectedOrders, setSelectedOrders] = useState([])
  const [selectedRun, setSelectedRun] = useState(null)
  const [selectedRunDetails, setSelectedRunDetails] = useState(null)
  const [loading, setLoading] = useState('')
  const { useOrderUnlock, useOrderLock, createNewRun, useAutoDispatchBuildingDetails, useAutoDispatchOverview } =
    useAutoDispatch()
  const {
    data: overviewData,
    isFetching: isFetchingOverviewData,
    refetch: refetchOverviewData,
  } = useAutoDispatchOverview()
  const { lockedOrders } = useLockedOrders()
  const { lockedSchedules } = useLockedSchedules()
  const { userRunState, runStatus } = useRouteOptimization()

  // For runs that are building, this endpoint is utilized to fetch schedule data
  const { data: buildingDetails, isFetching: isFetchingDetails } = useAutoDispatchBuildingDetails(
    mode !== 'CREATE' && mode !== 'REVIEW' && selectedRun?.id
  )

  useEffect(() => {
    setSelectedRunDetails(buildingDetails)
  }, [buildingDetails])

  const updatedItems = useMemo(() => {
    if (selectedRun) {
      const run = runStatus.find((run) => run.run_id === selectedRun.id)
      mode !== 'DEFAULT' && setSelectedRun({ ...selectedRun, ...run, status: run?.state })
    }
    if (overviewData && overviewData?.length !== runStatus?.length) {
      refetchOverviewData()
      return []
    }
    return overviewData?.map((item) => {
      const run = runStatus.find((run) => run.run_id === item.id)
      return { ...item, ...run, status: run?.state }
    })
  }, [overviewData, runStatus])

  useEffect(() => {
    const orderIds = lockedOrders?.filter((order) => order.run_id === selectedRun?.id).map((o) => o.order_id)
    const ordersToSet = selectedRunDetails?.orders?.filter((order) => orderIds?.includes(order.id))
    setSelectedOrders(ordersToSet)
  }, [selectedRunDetails])

  const handleSetSelectedRun = (run) => {
    setSelectedRun(run)
    const runState = run.state || run.status
    const newMode = runState === 'creating' ? 'CREATE' : runState === 'building' ? 'EDIT' : 'REVIEW'
    setMode(newMode)
    setSelectedRunDetails(run)
    setLoading('')
  }

  const refreshOptimizer = () => {
    refetchOrders()
    refetchDriverSchedule()
  }

  const resetOptimizer = (refresh?: boolean) => {
    if (refresh) refreshOptimizer()
    setSelectedRun(null)
    setSelectedOrders([])
    setMode(ROUTE_OPTIMIZER_MODE.REVIEW)
  }

  const closeWizard = (refresh: boolean) => {
    resetOptimizer(refresh)
    setVisible(false)
    setMode(null)
  }

  const startOptimizerRun = async () => {
    const payload = {
      date: schedulePayload.viewDate,
      shift: schedulePayload.shift,
      id: selectedRun?.id,
    }
    setLoading('optimizing')
    try {
      await api.automatedDispatchStart(payload)
      closeWizard(false)
    } catch (e) {
      NotificationMessage('Run Failed.', 'This run could not be started. Please try again.')
    } finally {
      setLoading('')
    }
  }

  const handleCreateNewRun = () => {
    setLoading('creating')
    const filteredOrdersFromDispatch = ordersFromDispatch?.filter((item) => item?.type !== 'Order Request (Requested)')
    createNewRun
      .mutateAsync({
        date: schedulePayload.viewDate,
        shift: schedulePayload.shift,
      })
      .then((res) => {
        if (res) {
          handleSetSelectedRun({
            id: res.id,
            schedules: res.shifts,
            orders: filteredOrdersFromDispatch,
            status: 'creating',
            state: 'creating',
          })
        }
      })
  }

  useEffect(() => {
    if (visible && mode === 'USER_REVIEW' && !selectedRun) {
      setMode(ROUTE_OPTIMIZER_MODE.REVIEW)
      setSelectedRun({ id: userRunState.pendingRun, status: 'pending' })
    }

    if (visible && mode === 'DEFAULT') {
      if (userRunState.buildingRuns?.length > 0) {
        const run = userRunState.buildingRuns
          .map((run) => {
            const runInfo = overviewData?.find((r) => r.id === run.run_id)?.created_on
            return { ...run, id: run.run_id, created_on: runInfo }
          })
          .sort((a, b) => (new Date(b.created_on) - new Date(a.created_on) > 1 ? 1 : -1))?.[0]

        setMode(ROUTE_OPTIMIZER_MODE.EDIT)
        setSelectedRun(run)
      } else {
        handleCreateNewRun()
      }
    }

    if (visible && mode === 'CREATE' && !selectedRun) {
      handleCreateNewRun()
    }
  }, [visible, mode, selectedRun])

  const handleOrderSelectionChanged = (selected) => {
    setSelectedOrders(selected)
    const unselectedOrders = selectedOrders?.filter((order) => !selected?.includes(order)).map((order) => order.id)
    if (unselectedOrders.length) {
      useOrderUnlock.mutate({ ids: unselectedOrders, run_id: selectedRun.id })
      return
    }
    const selectedIds = selected?.map((order) => order.id)
    if (selectedIds?.length) {
      useOrderLock.mutate({ ids: selectedIds, run_id: selectedRun.id })
    }
  }

  return (
    <Drawer
      placement='bottom'
      className='optimizer-wizard'
      open={visible}
      onClose={() => {
        closeWizard(false)
      }}
      destroyOnClose
      title={<DrawerTitle closeWizard={closeWizard} />}
      height='98%'
      styles={{ body: { padding: '0px', background: 'var(--bg-2)' } }}
      closable={false}
    >
      <Horizontal style={{ height: '100%' }}>
        <Vertical flex={1} className='border-right'>
          {mode === 'CREATE' && !selectedRun ? (
            <Horizontal className='full-height-width' verticalCenter horizontalCenter>
              <Spin size='large' />
            </Horizontal>
          ) : (
            <HistoryPanel
              setSelectedRun={setSelectedRun}
              selectedRun={selectedRun}
              mode={mode}
              setMode={setMode}
              loading={loading}
              data={overviewData}
              isFetching={isFetchingOverviewData}
              lockedOrders={lockedOrders}
              lockedSchedules={lockedSchedules}
              updatedItems={updatedItems}
            />
          )}
        </Vertical>
        <Vertical flex={4} style={{ height: '100%', width: '100%', background: 'var(--bg-2)' }}>
          {selectedRun && (mode === 'CREATE' || mode === 'EDIT') && (
            <CreateNewRun
              handleOrderSelectionChanged={handleOrderSelectionChanged}
              startOptimizerRun={startOptimizerRun}
              depots={depots}
              loading={isFetchingDetails || loading}
              orderData={selectedRunDetails?.orders}
              scheduleData={selectedRunDetails?.schedules}
              selectedRun={selectedRun}
              resetOptimizer={resetOptimizer}
            />
          )}
          {selectedRun && mode === 'REVIEW' && (
            <ReviewResults selectedRun={selectedRun} resetOptimizer={resetOptimizer} closeWizard={closeWizard} />
          )}
          {!selectedRun && mode === 'REVIEW' && (
            <Horizontal className='full-height-width' verticalCenter horizontalCenter>
              <Texto category='h4'>Select a Run or Create a New One</Texto>
            </Horizontal>
          )}
          {!selectedRun && mode === 'CREATE' && (
            <Vertical className='full-height-width' verticalCenter horizontalCenter>
              <Texto category='h4' className='mb-4'>
                Just a moment while we create a new run...
              </Texto>
              <Spin size='large' />
            </Vertical>
          )}
        </Vertical>
      </Horizontal>
    </Drawer>
  )
}

function DrawerTitle({ closeWizard }: Pick<ReviewResultsProps, 'closeWizard'>) {
  return (
    <Horizontal style={{ background: 'var(--theme-color-2)' }} justifyContent='space-between' verticalCenter>
      <Texto category='h5' appearance='white'>
        Optimizer Wizard
      </Texto>
      <div>
        <GraviButton
          theme2
          className='optimizer-close-button'
          buttonText={<CloseOutlined />}
          onClick={() => closeWizard(false)}
        />
      </div>
    </Horizontal>
  )
}
